/**
 * Outbound attribution for BeHot.Now.
 *
 * Every navigation that leaves behot.now is tagged with
 * `?utm_source=BeHot.Now` so the destination (and our own analytics when
 * traffic returns) can attribute the click back to us. Existing query
 * params are preserved and an existing `utm_source` is never overwritten.
 */

export const UTM_SOURCE = "BeHot.Now";

/**
 * Returns true when `href` points to a different origin than the current
 * page (i.e. it is a genuine outbound navigation). Relative links and
 * same-origin absolute links are not outbound.
 */
export function isExternalHref(href: string): boolean {
  try {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://behot.now";
    const url = new URL(href, origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    if (typeof window !== "undefined") return url.host !== window.location.host;
    return url.host !== new URL(origin).host;
  } catch {
    return false;
  }
}

/**
 * Append `utm_source=BeHot.Now` to an outbound href, preserving existing
 * params. Returns the original href unchanged for same-origin / non-http
 * links or when `utm_source` is already present.
 */
export function tagOutboundHref(href: string, source: string = UTM_SOURCE): string {
  if (!href) return href;
  try {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://behot.now";
    const url = new URL(href, origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return href;
    if (typeof window !== "undefined" && url.host === window.location.host) return href;
    if (url.searchParams.get("utm_source")) return href;
    url.searchParams.set("utm_source", source);
    return url.toString();
  } catch {
    return href;
  }
}
